import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import * as golfApi from '@/api/golf'
import { extractErrorMessage } from '@/helpers/error-parsers'
import type {
  GolfCarProfile,
  GolfCarAsset,
  GolfCarImage,
  GolfTariff,
  GolfTrip,
  CreateGolfCarProfilePayload,
  UpdateGolfCarProfilePayload,
  CreateGolfCarAssetPayload,
  UpdateGolfCarAssetPayload,
  BlockGolfCarAssetPayload,
  CreateGolfTariffPayload,
  WialonUnit,
} from '@/api/golf'

export const useGolfVehiclesStore = defineStore('golf-vehicles', () => {
  const profiles = ref<GolfCarProfile[]>([])
  const currentProfile = ref<GolfCarProfile | null>(null)
  const profileAssets = ref<GolfCarAsset[]>([])
  const profileImages = ref<GolfCarImage[]>([])
  const profileTariffs = ref<GolfTariff[]>([])
  const profileTrips = ref<GolfTrip[]>([])
  const wialonUnits = ref<WialonUnit[]>([])

  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const profileCount = computed(() => profiles.value.length)
  const activeProfiles = computed(() =>
    profiles.value.filter(p => p.status === 'active')
  )

  // ============ PROFILE ACTIONS ============

  const fetchProfiles = async (filters?: { status?: string }) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfCarProfiles(filters)
      const responseData = response.data.data as any
      if (responseData && responseData.data && Array.isArray(responseData.data)) {
        profiles.value = responseData.data
      } else if (Array.isArray(responseData)) {
        profiles.value = responseData
      } else {
        profiles.value = []
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch profiles')
      console.error('[Golf Vehicles] Error fetching profiles:', e)
      profiles.value = []
    } finally {
      isLoading.value = false
    }
  }

  const fetchProfile = async (id: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfCarProfile(id)
      currentProfile.value = response.data.data || null
      return currentProfile.value
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch profile')
      console.error('Error fetching profile:', e)
      return null
    } finally {
      isLoading.value = false
    }
  }

  const fetchProfileTrips = async (id: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfCarProfileTrips(id)
      const responseData = response.data.data as any
      const data = responseData && responseData.data ? responseData.data : responseData
      profileTrips.value = Array.isArray(data) ? data : []
      return profileTrips.value
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch profile trips')
      console.error('Error fetching profile trips:', e)
      profileTrips.value = []
    } finally {
      isLoading.value = false
    }
  }

  const createProfile = async (payload: CreateGolfCarProfilePayload) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.createGolfCarProfile(payload)
      const newProfile = response.data.data
      if (newProfile) {
        ElMessage.success('Golf car profile created successfully')
        await fetchProfiles()
        return newProfile
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to create profile')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error creating profile:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const updateProfile = async (
    id: string,
    payload: UpdateGolfCarProfilePayload
  ) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.updateGolfCarProfile(id, payload)
      const updated = response.data.data
      const merged = { ...payload, ...(updated || {}) }

      const index = profiles.value.findIndex(p => p.id === id)
      if (index !== -1) {
        profiles.value[index] = { ...profiles.value[index], ...merged }
      }
      if (currentProfile.value?.id === id) {
        currentProfile.value = { ...currentProfile.value, ...merged }
      }

      if (updated) {
        ElMessage.success('Profile updated successfully')
      }
      return merged
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to update profile')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error updating profile:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const blockProfile = async (id: string, comment: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.blockGolfCarProfile(id, comment)
      const updated = response.data.data
      const merged: any = { ...(updated || {}) }
      merged.status = 'admin_blocked'

      const index = profiles.value.findIndex(p => p.id === id)
      if (index !== -1) {
        profiles.value[index] = { ...profiles.value[index], ...merged }
      }
      if (currentProfile.value?.id === id) {
        currentProfile.value = { ...currentProfile.value, ...merged }
      }

      if (updated) {
        ElMessage.success('Profile blocked successfully')
      }
      return merged
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to block profile')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error blocking profile:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const unblockProfile = async (id: string, comment?: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.unblockGolfCarProfile(id, comment)
      const updated = response.data.data
      const merged: any = { ...(updated || {}) }
      merged.status = 'active'

      const index = profiles.value.findIndex(p => p.id === id)
      if (index !== -1) {
        profiles.value[index] = { ...profiles.value[index], ...merged }
      }
      if (currentProfile.value?.id === id) {
        currentProfile.value = { ...currentProfile.value, ...merged }
      }

      if (updated) {
        ElMessage.success('Profile unblocked successfully')
      }
      return merged
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to unblock profile')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error unblocking profile:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  // ============ ASSET ACTIONS ============

  const createAsset = async (
    profileId: string,
    payload: CreateGolfCarAssetPayload
  ) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.createGolfCarAsset(profileId, payload)
      const newAsset = response.data.data
      if (newAsset) {
        profileAssets.value.push(newAsset)
        if (currentProfile.value) {
          currentProfile.value.assets = [...(currentProfile.value.assets || []), newAsset]
        }
        ElMessage.success('Asset created successfully')
        return newAsset
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to create asset')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error creating asset:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const updateAsset = async (
    id: string,
    payload: UpdateGolfCarAssetPayload
  ) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.updateGolfCarAsset(id, payload)
      const updated = response.data.data
      const merged = { ...payload, ...(updated || {}) }

      const index = profileAssets.value.findIndex(a => a.id === id)
      if (index !== -1) {
        profileAssets.value[index] = { ...profileAssets.value[index], ...merged }
      }
      if (currentProfile.value?.assets) {
        const aIndex = currentProfile.value.assets.findIndex(a => a.id === id)
        if (aIndex !== -1) {
          currentProfile.value.assets[aIndex] = { ...currentProfile.value.assets[aIndex], ...merged }
        }
      }

      // Also update in profiles array so CreateModel gets the latest state
      for (let pIndex = 0; pIndex < profiles.value.length; pIndex++) {
        if (profiles.value[pIndex].assets) {
          const aIndex = profiles.value[pIndex].assets!.findIndex((a: any) => a.id === id)
          if (aIndex !== -1) {
            profiles.value[pIndex].assets![aIndex] = { ...profiles.value[pIndex].assets![aIndex], ...merged }
            break
          }
        }
      }

      if (updated) {
        ElMessage.success('Asset updated successfully')
      }
      return merged
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to update asset')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error updating asset:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const blockAsset = async (id: string, payload: BlockGolfCarAssetPayload) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.blockGolfCarAsset(id, payload)
      const updated = response.data.data
      const merged: any = { ...(updated || {}) }

      const index = profileAssets.value.findIndex(a => a.id === id)
      if (index !== -1) {
        profileAssets.value[index] = { ...profileAssets.value[index], ...merged }
      }
      if (currentProfile.value?.assets) {
        const aIndex = currentProfile.value.assets.findIndex(a => a.id === id)
        if (aIndex !== -1) {
          currentProfile.value.assets[aIndex] = { ...currentProfile.value.assets[aIndex], ...merged }
        }
      }

      if (updated) {
        ElMessage.success('Asset blocked successfully')
      }
      return merged
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to block asset')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error blocking asset:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const unblockAsset = async (id: string, comment?: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.unblockGolfCarAsset(id, comment)
      const updated = response.data.data
      const merged: any = { ...(updated || {}) }

      const index = profileAssets.value.findIndex(a => a.id === id)
      if (index !== -1) {
        profileAssets.value[index] = { ...profileAssets.value[index], ...merged }
      }
      if (currentProfile.value?.assets) {
        const aIndex = currentProfile.value.assets.findIndex(a => a.id === id)
        if (aIndex !== -1) {
          currentProfile.value.assets[aIndex] = { ...currentProfile.value.assets[aIndex], ...merged }
        }
      }

      if (updated) {
        ElMessage.success('Asset unblocked successfully')
      }
      return merged
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to unblock asset')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error unblocking asset:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  // ============ IMAGE ACTIONS ============

  const createImage = async (
    profileId: string,
    formData: FormData
  ) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.createGolfCarImage(profileId, formData)
      const newImage = response.data.data
      if (newImage) {
        profileImages.value.push(newImage)
        if (currentProfile.value) {
          currentProfile.value.images = [...(currentProfile.value.images || []), newImage]
        }
        ElMessage.success('Image added successfully')
        return newImage
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to add image')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error adding image:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const deleteImage = async (imageId: string) => {
    isLoading.value = true
    error.value = null
    try {
      await golfApi.deleteGolfCarImage(imageId)
      profileImages.value = profileImages.value.filter(
        img => img.id !== imageId
      )
      if (currentProfile.value?.images) {
        currentProfile.value.images = currentProfile.value.images.filter(
          img => img.id !== imageId
        )
      }
      ElMessage.success('Image deleted successfully')
      return true
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to delete image')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error deleting image:', e)
    } finally {
      isLoading.value = false
    }
    return false
  }

  // ============ TARIFF ACTIONS ============

  const fetchTariffs = async () => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfTariffs()
      const responseData = response.data.data as any
      const data = responseData && responseData.data ? responseData.data : responseData
      profileTariffs.value = Array.isArray(data) ? data : []
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch tariffs')
      console.error('Error fetching tariffs:', e)
    } finally {
      isLoading.value = false
    }
  }

  const createTariff = async (payload: CreateGolfTariffPayload) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.createGolfTariff(payload)
      const responseData = response.data.data as any
      const newTariff = responseData && responseData.data ? responseData.data : responseData
      if (newTariff) {
        profileTariffs.value.push(newTariff)
        ElMessage.success('Tariff created successfully')
        return newTariff
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to create tariff')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error creating tariff:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const updateTariff = async (
    id: string,
    payload: Partial<CreateGolfTariffPayload>
  ) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.updateGolfTariff(id, payload)
      const responseData = response.data.data as any
      const updated = responseData && responseData.data ? responseData.data : responseData
      if (updated) {
        profileTariffs.value = profileTariffs.value.map(t =>
          t.id === id ? { ...t, ...updated } : t
        )
        ElMessage.success('Tariff updated successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to update tariff')
      ElMessage.error({
        message: error.value,
        duration: 6000,
        showClose: true,
      })
      console.error('Error updating tariff:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  // ============ RESET ============

  const resetError = () => {
    error.value = null
  }

  const clearCurrentProfile = () => {
    currentProfile.value = null
    profileAssets.value = []
    profileImages.value = []
    profileTariffs.value = []
    profileTrips.value = []
  }

  const fetchWialonUnits = async () => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getWialonUnits()
      wialonUnits.value = response.data.data
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch Wialon units')
    } finally {
      isLoading.value = false
    }
  }

  return {
    profiles,
    currentProfile,
    profileAssets,
    profileImages,
    profileTariffs,
    profileTrips,
    wialonUnits,
    isLoading,
    error,

    profileCount,
    activeProfiles,

    fetchProfiles,
    fetchProfile,
    fetchProfileTrips,
    fetchWialonUnits,
    createProfile,
    updateProfile,
    blockProfile,
    unblockProfile,
    createAsset,
    updateAsset,
    blockAsset,
    unblockAsset,
    createImage,
    deleteImage,
    fetchTariffs,
    createTariff,
    updateTariff,
    resetError,
    clearCurrentProfile,
  }
})
